// URL / route parsing for the SPA router. Everything here is render-free:
// pure derivations from the current URL (or a route string), shared by
// App.tsx composition, the route components, and the navigation hook.
//
// Routing is pathname-first: canonical routes live in the path (+ a small set
// of screen-level query params, e.g. `/studio/capsules/<id>?step=build`), and
// the hash fragment is reserved for a short list of TRANSIENT data
// (endpoint/token/continuation_*/waitlist). `route=`-carrying hash URLs are
// still READ for backward compatibility (legacy Desktop/CLI links, old
// bookmarks) and canonicalized to real paths on first load — but no new
// `route=` URL is ever generated.
import { parseFragment } from "../../api/endpoint";
import type { WizardStep } from "@/components/submission/wizardSteps";

/** Old billing bookmarks remain informational during the Pro waitlist. */
function billingRouteAlias(path: string): string {
  if (path === "/cloud") return "/settings/billing/plans";
  if (path === "/cloud/return") return "/settings/billing/return";
  return path;
}

// Stable PATH routes (no hash) — `/a/<slug>` (the App Page). Autostart-on-
// arrival rides `?autostart=1` (Home's Run button, Stripe checkout returns).
// `not_found_handling = "single-page-application"` (wrangler.toml) serves
// index.html for these so the SPA boots and this router takes over.
//
// The `/open/<capsule_ref>` URL and the `?launch=1` alias are RETIRED with no
// compat rewrite: `/open/*` is just an unknown path (this router recognizes
// nothing there), and a `?launch=1` that somehow reaches the App Page is
// simply an unknown query param — nothing rewrites or honors it.

/**
 * The two App Page shapes, parsed off a route string (not the live URL):
 *
 *   /a/<slug>                    → { publisher: null, slug }
 *   /a/by/<publisher>/<slug>     → { publisher, slug }
 *
 * The bare form stays the canonical, shareable short link. The `by` form is
 * what the Store's own surfaces mint (GET /v1/store/home hero CTAs) because a
 * slug is only unique per-publisher — it resolves exactly one capsule instead
 * of the bare lookup's oldest-public-match. Segments are URI-encoded on the
 * way in and decoded here. Returns null when the route isn't an App Page, or
 * when either segment is empty.
 */
export function parseAppPageRoute(
  route: string,
): { publisher: string | null; slug: string } | null {
  const path = route.split("?")[0];
  if (!path.startsWith("/a/")) return null;
  const rest = path.slice("/a/".length).replace(/\/+$/, "");
  if (rest === "by" || rest.startsWith("by/")) {
    const segments = rest.slice("by/".length).split("/");
    if (segments.length !== 2) return null;
    const publisher = decodeURIComponent(segments[0]);
    const slug = decodeURIComponent(segments[1]);
    if (!publisher || !slug) return null;
    return { publisher, slug };
  }
  const slug = decodeURIComponent(rest);
  return slug.length > 0 ? { publisher: null, slug } : null;
}

/**
 * A Personal App's in-PWA address. Instance ids occupy a reserved `cinst_`
 * namespace, so this remains disjoint from human-authored App slugs while
 * sharing the user-facing `/a/<id>` route family.
 */
export function parseInstanceAppRoute(
  route: string,
): { instanceId: string } | null {
  const app = parseAppPageRoute(route);
  if (
    !app ||
    app.publisher !== null ||
    !/^cinst_[A-Za-z0-9]+$/.test(app.slug)
  ) {
    return null;
  }
  return { instanceId: app.slug };
}

/** Operator-curated Discover listings use their stable listing id directly. */
export function parseDiscoverAppRoute(
  route: string,
): { discoverAppId: string } | null {
  const app = parseAppPageRoute(route);
  if (
    !app ||
    app.publisher !== null ||
    !/^discover-[A-Za-z0-9_-]+$/.test(app.slug)
  ) {
    return null;
  }
  return { discoverAppId: app.slug };
}

/**
 * Normalize a route string into a safe, same-origin internal path.
 *
 * Used both to validate a legacy `route=` fragment value and to sanitize
 * post-OAuth navigation targets. Enforces the internal-path rules:
 *   - a missing leading "/" is added (bare `run` → `/run`)
 *   - `//evil.example`, an absolute URL, a backslash, NUL, and control chars
 *     are all rejected → `/`
 *   - a trailing slash is stripped (root stays `/`)
 * A query string (e.g. `?step=build`) is preserved verbatim.
 */
export function normalizeInternalRoute(route: string): string {
  const qIdx = route.indexOf("?");
  const path = qIdx >= 0 ? route.slice(0, qIdx) : route;
  const query = qIdx >= 0 ? route.slice(qIdx) : "";

  let p = path;
  // Absolute URL with a scheme (http:, https:, etc.) is never internal.
  if (/^[a-z][a-z0-9+.-]*:/i.test(p)) return "/";
  if (!p.startsWith("/")) p = `/${p}`;
  // Protocol-relative or double-slash paths are never internal.
  if (p.startsWith("//")) return "/";
  if (p.includes("\\")) return "/";
  if (/[\u0000-\u001f]/.test(p)) return "/";
  if (p === "/") return "/" + query;
  return billingRouteAlias(p.replace(/\/+$/, "")) + query;
}

/**
 * Derive the canonical route string from a pathname + search pair.
 *   - trailing slashes are normalized away (root stays `/`)
 *   - `/` returns `/`
 *   - only `/studio/capsules/<id>` carries its `step` query into the route
 *     string (for backward compat with the existing router)
 *   - screen-routing-agnostic queries (`autostart`, `q`, `surface`, `sort`,
 *     `source`) are deliberately NOT part of the route string
 *   - an unknown pathname is returned as-is (never collapsed to `/`), so the
 *     honest "Unknown route" fallback can render
 */
export function routeFromPathAndSearch(
  pathname: string,
  search: string,
): string {
  const path = billingRouteAlias(
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname,
  );
  if (path === "/") return "/";
  if (path.startsWith("/studio/capsules/")) {
    const step = new URLSearchParams(search).get("step");
    if (step) return `${path}?step=${step}`;
  }
  return path;
}

export function parseRoute(): string {
  const legacy = parseFragment();

  // Backward compat: a legacy `route=` fragment is still honored on first
  // read. It's normalized through the same internal-path rules that govern
  // real paths (so `//evil.example` → `/`, bare `run` → `/run`).
  if (legacy.route) {
    return normalizeInternalRoute(legacy.route);
  }

  return routeFromPathAndSearch(
    window.location.pathname,
    window.location.search,
  );
}

/**
 * Migrate a legacy `#route=...` URL to a canonical real path, IN PLACE
 * (history.replaceState — never pushes a history entry).
 *
 * `route` and `source` move out of the fragment (into the path and a
 * `source=` query respectively). `endpoint` and `token` are DROPPED: they fed
 * the retired Desktop Runtime credential lane, so nothing consumes them any
 * more and leaving a bearer token in the address bar, history and referrer is
 * pure liability. Every other fragment param — continuation_*, waitlist_*,
 * etc. — is PRESERVED, so a consumer reading it on mount still sees it after
 * navigation canonicalizes the URL.
 *
 * Non-internal `route` values (//evil.example, absolute URLs, backslash/NUL,
 * control chars) are rejected → `/` (the value is dropped, never honored).
 */
export function canonicalizeLegacyHashRoute(): void {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return;
  const params = new URLSearchParams(hash);
  const rawRoute = params.get("route");
  const hadCredentials = params.has("endpoint") || params.has("token");
  // Retired Desktop Runtime credential lane: strip unconditionally, even on a
  // fragment that carries no legacy `route` to migrate.
  params.delete("endpoint");
  params.delete("token");
  if (rawRoute === null) {
    if (hadCredentials) {
      const remaining = params.toString();
      history.replaceState(
        null,
        "",
        window.location.pathname +
          window.location.search +
          (remaining ? `#${remaining}` : ""),
      );
    }
    return;
  }

  const route = normalizeInternalRoute(rawRoute);
  const source = params.get("source");
  params.delete("route");
  params.delete("source");

  let target = route;
  if (source) {
    target +=
      (target.includes("?") ? "&" : "?") +
      `source=${encodeURIComponent(source)}`;
  }
  const remaining = params.toString();
  const url = target + (remaining ? `#${remaining}` : "");
  history.replaceState(null, "", url);
}

const STUDIO_STEPS = new Set<WizardStep>([
  "source",
  "app_info",
  "build",
  "verify",
  "review",
]);

export function parseStudioCapsuleRoute(route: string): {
  studioCapsuleId: string;
  requestedStep: WizardStep | null;
} {
  const rest = route.slice("/studio/capsules/".length);
  const [rawId, query] = rest.split("?");
  const stepParam = query ? new URLSearchParams(query).get("step") : null;
  const requestedStep =
    stepParam && STUDIO_STEPS.has(stepParam as WizardStep)
      ? (stepParam as WizardStep)
      : null;
  return {
    studioCapsuleId: decodeURIComponent(rawId),
    requestedStep,
  };
}

/** `/studio/capsules/:id/analytics` → the capsule id. Returns null when the
 *  route is not the analytics path (so the generic parser owns the rest). */
export function parseStudioCapsuleAnalyticsRoute(
  route: string,
): { studioCapsuleId: string } | null {
  const prefix = "/studio/capsules/";
  if (!route.startsWith(prefix)) return null;
  const rest = route.slice(prefix.length);
  const rawPath = rest.split("?")[0];
  const suffix = "/analytics";
  if (!rawPath.endsWith(suffix)) return null;
  const rawId = rawPath.slice(0, -suffix.length);
  if (!rawId || rawId.includes("/")) return null;
  return { studioCapsuleId: decodeURIComponent(rawId) };
}

/**
 * `/invite/<token>` — the recipient's side of a shared App.
 *
 * A PATH route, and a public one: the person following it may not be signed in
 * yet, and may not even have an account. Anything hash-routed or gated would
 * make a legitimate invite indistinguishable from a broken link.
 */
export function parseInviteRoute(route: string): { token: string } | null {
  const path = route.split("?")[0].replace(/\/+$/, "");
  if (!path.startsWith("/invite/")) return null;
  const token = decodeURIComponent(path.slice("/invite/".length));
  // Shape-checked here so a truncated paste fails as "not a link" on the
  // client, without a round trip that would look like the invite existed.
  return /^[0-9a-f]{40}$/.test(token) ? { token } : null;
}

export type PersonalAppsRoute =
  | { kind: "list" }
  | { kind: "new" }
  | { kind: "ready"; instanceId: string }
  | { kind: "open"; instanceId: string }
  | { kind: "detail"; instanceId: string };

/**
 * Personal Apps routing. Home (`/`) IS the list — My Apps and Discover live on
 * one screen there (`?view=` selects a pane). The former `/apps` and
 * `/apps/discover` paths are retired with no redirect; only the action/detail
 * sub-paths remain: `/apps/new` is Add App, `/apps/<id>/ready` is the moment
 * just after adding, `/apps/<id>/open` resolves a saved-app launch link, and `/apps/<id>`
 * is one owned App.
 *
 * `ready` is its own route rather than a flag on the detail screen because it
 * is the one place the product says "you do not need to come back here" — and
 * a person who reloads or shares that URL should get that message again, not a
 * settings page.
 */
export function parsePersonalAppsRoute(
  route: string,
): PersonalAppsRoute | null {
  if (route === "/") return { kind: "list" };
  if (route === "/apps/new") return { kind: "new" };
  // Retired alias, explicitly refused so it can never be read as an instance
  // detail for an app literally named "discover".
  if (route === "/apps/discover") return null;
  if (!route.startsWith("/apps/")) return null;
  const rest = route.slice("/apps/".length).split("?")[0];
  if (rest.endsWith("/ready")) {
    const id = decodeURIComponent(rest.slice(0, -"/ready".length));
    return id ? { kind: "ready", instanceId: id } : null;
  }
  if (rest.endsWith("/open")) {
    const id = decodeURIComponent(rest.slice(0, -"/open".length));
    return id ? { kind: "open", instanceId: id } : null;
  }
  const id = decodeURIComponent(rest);
  return id && !id.includes("/") ? { kind: "detail", instanceId: id } : null;
}
